import { useForm, usePage } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import type { SharedProps } from '@/types';

// The react-hook-form/zod form of the Next page on Inertia's useForm (PORTING.md U2).
// The success message is the redirect's flash, rendered in the same green box the
// server action's `serverState.message` filled.
export function CredentialUrlForm({
  action,
  currentUrl,
  customAllowed,
  eventEndsAt,
  idHostSlug,
  customHint,
  dateLocale,
}: {
  action: string;
  currentUrl: string | null;
  customAllowed: boolean;
  eventEndsAt: string | null;
  idHostSlug: string;
  customHint: string;
  dateLocale: string;
}) {
  const { flash, errors } = usePage().props as unknown as SharedProps & {
    errors: Record<string, string | string[]>;
  };
  const form = useForm({
    mode: currentUrl ? 'custom' : 'default',
    credentialCustomUrl: currentUrl ?? '',
  });

  const first = (value: string | string[] | undefined) =>
    Array.isArray(value) ? value[0] : value;
  const globalError = first(errors.base);
  const urlError = first(errors.credentialCustomUrl);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        form.post(action, { preserveScroll: true });
      }}
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-2">
        <Label className="font-bold text-primary text-sm uppercase tracking-wide">
          A dónde apunta tu QR
        </Label>
        <RadioGroup
          value={form.data.mode}
          onValueChange={(value) => form.setData('mode', value)}
          className="flex flex-col gap-3"
        >
          <label
            htmlFor="mode-default"
            className="flex cursor-pointer items-start gap-3 border-2 border-primary/40 p-4 hover:border-primary"
          >
            <RadioGroupItem id="mode-default" value="default" />
            <div className="flex flex-col gap-1">
              <span className="font-bold text-primary text-sm">
                Página del hackathon (por defecto)
              </span>
              <span className="font-mono text-primary/70 text-xs">
                Tu perfil en hack.platan.us/{idHostSlug}/id/...
              </span>
            </div>
          </label>

          <label
            htmlFor="mode-custom"
            className={cn(
              'flex cursor-pointer items-start gap-3 border-2 p-4',
              customAllowed
                ? 'border-primary/40 hover:border-primary'
                : 'cursor-not-allowed border-primary/20 opacity-60',
            )}
          >
            <RadioGroupItem
              id="mode-custom"
              value="custom"
              disabled={!customAllowed}
            />
            <div className="flex flex-col gap-1">
              <span className="font-bold text-primary text-sm">
                URL personalizada
              </span>
              <span className="font-mono text-primary/70 text-xs">
                {customAllowed
                  ? customHint
                  : eventEndsAt
                    ? `Disponible después del ${new Date(eventEndsAt).toLocaleString(dateLocale)}.`
                    : 'Disponible cuando termine el evento.'}
              </span>
            </div>
          </label>
        </RadioGroup>
      </div>

      {form.data.mode === 'custom' && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="credentialCustomUrl">URL personalizada</Label>
          <Input
            id="credentialCustomUrl"
            name="credentialCustomUrl"
            type="url"
            placeholder="https://tu-sitio.com"
            disabled={form.processing || !customAllowed}
            value={form.data.credentialCustomUrl}
            onChange={(event) =>
              form.setData('credentialCustomUrl', event.target.value)
            }
          />
          {urlError && (
            <p className="font-medium text-destructive text-sm">{urlError}</p>
          )}
        </div>
      )}

      {globalError && (
        <p className="border-2 border-red-500 bg-red-500/10 p-3 font-mono text-red-500 text-sm">
          {globalError}
        </p>
      )}

      {flash.notice && (
        <p className="border-2 border-primary bg-primary/10 p-3 font-mono text-primary text-sm">
          {flash.notice}
        </p>
      )}

      <Button
        type="submit"
        disabled={form.processing}
        className="w-full border-2 border-primary bg-primary font-mono text-background hover:bg-primary/90"
      >
        {form.processing ? 'Guardando...' : 'Guardar'}
      </Button>
    </form>
  );
}
